import { Component, Prop, h, Element, Host, State, Watch, Event, EventEmitter } from '@stencil/core';
import { Root, SelectItem, PredefinedValues } from './types';
import debounce from 'debounce';
import { v4 as uuidv4 } from 'uuid';
import { minus } from 'pn-design-assets/pn-assets/icons.js';

@Component({
  tag: 'pn-multi-row-connected-dropdown-row',
  styleUrl: 'pn-multi-row-connected-dropdown-row.scss',
})
export class PnMultiRowConnectedDropdownRow {
  @Element() hostElement: Element;
  private _dropdownData: Root[];
  private _predefinedValue: PredefinedValues;
  pnConnectedDropdown: HTMLElement = null;
  pnConnectedDropdownSlotElement: HTMLElement = null;
  pnAddFieldButton: HTMLElement = null;
  pnFirstDropdown: HTMLElement = null;
  pnSecondDropdown: HTMLElement = null;
  pnInputField: HTMLElement = null;
  pnSingleDatePicker: HTMLElement = null;
  pnStartDate: HTMLElement = null;
  pnEndDate: HTMLElement = null;
  pnShowDateRangeFiledButton: HTMLElement = null;
  pnDateRangeContainer: HTMLElement = null;
  pnGenerateDatesButton: HTMLElement = null;
  pnDaysRow: HTMLElement = null;
  pnGeneratedDatesSlot: HTMLElement = null;
  pnSaveGeneratedDatesButton: HTMLElement = null;
  pnDeleteRowButton: HTMLElement = null;

  @Prop() dropdownData: string | Root[] = null;
  @Prop() languageCode: string = null;
  @Prop() label: string = null;
  @Prop() firstDropdownLabel: string = null;
  @Prop() secondDropdownLabel: string = null;
  @Prop() dropdownPlaceholder: string = null;
  @Prop() addRowText: string = null;
  @Prop() inputFieldLabel: string = null;
  @Prop() inputFieldPlaceholder: string = null;
  @Prop() disabledDates: string = null;
  @Prop() dateButtonText: string = null;
  @Prop() datePlaceholder: string = null;
  @Prop() dateDaysFromToday: number = 1;
  @Prop() allowMultipleDates: boolean = null;
  @Prop() startDateLabel: string = null;
  @Prop() endDateLabel: string = null;
  @Prop() multiDateHelperText: string = null;
  @Prop() generateDatesButtonText: string = null;
  @Prop() saveDateButtonText: string = null;
  @Prop() noDatesText: string = null;
  @Prop() index: number = null;
  @Prop() predefinedValue: string = null;
  @Prop() yearsToAddToEndDate: number = null;
  @Prop() minAmount: number = null;
  @Prop() errorMessageEmpty: string;
  @Prop() errorMessageMinAmount: string;
  @Prop() minWarningAmount: number = null;
  @Prop() warningMessageMinAmount: string;

  @State() buttonsArray: SelectItem[] = null;
  @State() firstDropdownArr: SelectItem[] = null;
  @State() secondDropdownArr: SelectItem[] = [];
  @State() firstDropdownClass: string = null;
  @State() secondDropdownClass: string = null;
  @State() firstValidDateString: string = '';
  @State() lastValidDateString: string = '';
  @State() firstDropdownPlaceholder: string = '-';
  @State() secondDropdownPlaceholder: string = '-';
  @State() startDateValue: string = '';
  @State() endDateValue: string = '';
  @State() generatedDatesArr: string[] = [];
  @State() selectedButtonValue: string = '';
  @State() inputFieldValue: string = '';
  @State() dateValue: string = '';
  @State() daysArr: SelectItem[] = null;

  @Event() rowValueChange: EventEmitter<PredefinedValues>;
  @Event() deleteRowEvent: EventEmitter<string>;

  componentWillLoad() {
    this.getPredefinedValues();
    this.setDropDownData();
    this.setFirstValidDate();
    this.setInitialValues();
  }

  componentDidLoad() {
    this.addEventHandlers();
    this.emitRowValues();
  }

  setDropDownData() {
    if (typeof this.dropdownData === 'string') {
      this._dropdownData = JSON.parse(this.dropdownData);
    } else {
      this._dropdownData = this.dropdownData;
    }
  }

  getPredefinedValues() {
    this._predefinedValue = JSON.parse(this.predefinedValue);
  }

  @Watch('predefinedValue')
  applyPredefinedValue() {
    this.getPredefinedValues();
  }

  setInitialValues() {
    this.setButtonsValue();
    this.setFirstDropdownValues();
    this.setSecondDropdownValues();
    this.setInputField();
    this.setDateValue();
    this.setDateRangeDefaults();

    this.firstDropdownClass = this.firstDropdownLabel.replace(' ', '_') + '-dropdown';
    this.secondDropdownClass = this.secondDropdownLabel.replace(' ', '_') + '-dropdown';
  }

  setButtonsValue() {
    this.buttonsArray = [];

    if (this._predefinedValue.buttons?.length) {
      this.buttonsArray = this._predefinedValue.buttons;

      this.buttonsArray.forEach(item => {
        if (item.selected) {
          this.selectedButtonValue = item.value;
        }
      });
    } else {
      this._dropdownData.forEach(item => {
        const button: SelectItem = { selected: false, value: item.label, label: item.label };
        this.buttonsArray.push(button);
      });
    }
  }

  setFirstDropdownValues() {
    this.firstDropdownArr = [];

    if (this._predefinedValue.firstDropdown?.length) {
      this.firstDropdownArr = [...this._predefinedValue.firstDropdown];
    }
  }

  setSecondDropdownValues() {
    this.secondDropdownArr = [];

    if (this._predefinedValue.secondDropdown?.length) {
      this.secondDropdownArr = [...this._predefinedValue.secondDropdown];
    }
  }

  setInputField() {
    this.inputFieldValue = this._predefinedValue.input?.length ? this._predefinedValue.input : '';
  }

  setDateValue() {
    if (this.allowMultipleDates) {
      if (this.pnDateRangeContainer) {
        this.pnDateRangeContainer.style.display = this._predefinedValue.dates["start"]?.length ? '' : 'none';
      }
      this.generatedDatesArr = this._predefinedValue.dates["start"]?.length ? this._predefinedValue.dates["start"].split(',') : [];
      this.saveGeneratedDates();
    } else {
      this.dateValue = this._predefinedValue.dates["start"]?.length ? this._predefinedValue.dates["start"] : '';
    }
  }

  setDateRangeDefaults() {
    this.daysArr = this._predefinedValue.dateRange.days;
    this.startDateValue = this._predefinedValue.dateRange.startDate?.length ? this._predefinedValue.dateRange.startDate : '';
    this.endDateValue = this._predefinedValue.dateRange.endDate?.length ? this._predefinedValue.dateRange.endDate : '';
  }

  setFirstValidDate() {
    const yearsToAdd = this.yearsToAddToEndDate ? this.yearsToAddToEndDate : 2;
    const date = new Date();
    date.setDate(date.getDate() + this.dateDaysFromToday);

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const endYear = year + yearsToAdd;

    this.firstValidDateString = `${year.toString()}-${month}-${day}`;
    this.lastValidDateString = `${endYear.toString()}-${month}-${day}`;
  }

  addEventHandlers() {
    const emitValues = debounce(this.emitRowValues.bind(this), 500);

    this.pnInputField.addEventListener('keyup', this.handleInput.bind(this));
    this.pnInputField.addEventListener('change', this.handleInput.bind(this));
    this.pnInputField.addEventListener('input', this.handleInput.bind(this));
    this.pnInputField.addEventListener('keyup', emitValues);
    this.pnInputField.addEventListener('change', emitValues);
    this.pnInputField.addEventListener('input', emitValues);
    this.pnFirstDropdown.addEventListener('change', this.handleFirstDropdownChange.bind(this));
    this.pnSecondDropdown.addEventListener('change', this.handleSecondDropdownChange.bind(this));

    if (this.allowMultipleDates) {
      this.pnShowDateRangeFiledButton.addEventListener('click', this.showDateRangeRow.bind(this));
      this.pnGenerateDatesButton.addEventListener('click', this.generateDates.bind(this));
      this.pnSaveGeneratedDatesButton.addEventListener('click', this.saveGeneratedDates.bind(this));
      this.pnSaveGeneratedDatesButton.addEventListener('click', emitValues);
      this.pnStartDate.addEventListener('dateselection', this.handleDateSelection.bind(this));
      this.pnEndDate.addEventListener('dateselection', this.handleDateSelection.bind(this));
    } else {
      this.pnSingleDatePicker.addEventListener('dateselection', this.handleDateSelection.bind(this));
      this.pnSingleDatePicker.addEventListener('dateselection', emitValues);
    }

    if (this.index > 0) {
      this.pnDeleteRowButton.addEventListener('click', this.deleteRow.bind(this));
    }
  }

  generateUniqueId() {
    return uuidv4();
  }

  updateSelectItemArr(arr: SelectItem[], val: string) {
    arr.forEach(item => {
      if (item.value === val) {
        item.selected = !item.selected;
      } else {
        item.selected = false;
      }
    });
    return arr;
  }

  handleOptionButtonClick(e) {
    e.preventDefault();
    const elmValue = e.target.innerHTML;

    this.firstDropdownArr = [];
    this.secondDropdownArr = [];
    this.firstDropdownPlaceholder = '-';
    this.secondDropdownPlaceholder = '-';

    this.buttonsArray = [...this.updateSelectItemArr(this.buttonsArray, elmValue)];
    this.selectedButtonValue = this.buttonsArray.some(item => item.selected) ? elmValue : '';

    if (this.selectedButtonValue?.length) {
      const selectionChildren = this._dropdownData.find(arr => arr.label === elmValue);
      if (selectionChildren) {
        this.firstDropdownArr = selectionChildren.values.map(item => ({
          selected: false,
          value: item.label,
          label: item.label
        }));
      }
      this.firstDropdownPlaceholder = this.dropdownPlaceholder;

      this.secondDropdownArr = [];
      this.secondDropdownPlaceholder = '-';
    } else {
      this.firstDropdownArr = [];
      this.firstDropdownPlaceholder = '-';
      this.secondDropdownArr = [];
      this.secondDropdownPlaceholder = '-';
    }

    const firstNativeSelect = this.pnFirstDropdown.querySelector('select');
    if (firstNativeSelect) {
      firstNativeSelect.value = "";
    }
    const secondNativeSelect = this.pnSecondDropdown.querySelector('select');
    if (secondNativeSelect) {
      secondNativeSelect.value = "";
    }

    const firstDropdownText = this.pnFirstDropdown.querySelector('.pn-select option[disabled]');
    if (firstDropdownText) {
      firstDropdownText.textContent = this.firstDropdownPlaceholder;
    }

    const secondDropdownText = this.pnSecondDropdown.querySelector('.pn-select option[disabled]');
    if (secondDropdownText) {
      secondDropdownText.textContent = this.secondDropdownPlaceholder;
    }
  }

  handleFirstDropdownChange(e: CustomEvent) {
    var target = e.target as HTMLSelectElement;

    const selectedValue = target.value;
    this.firstDropdownArr = [...this.updateSelectItemArr(this.firstDropdownArr, selectedValue)];

    this.secondDropdownArr = [];
    const selectedButton = this.buttonsArray.findIndex(item => item.selected === true);
    const firstChildren = this._dropdownData.find(arr => arr.label === this.buttonsArray[selectedButton].value);
    const secondChildren = firstChildren.values.find(arr => arr.label === selectedValue);
    secondChildren.values.forEach(item => {
      this.secondDropdownArr.push({ selected: false, value: item.label, label: item.label });
    });
    
    const secondDropdownText = this.pnSecondDropdown.querySelector('.pn-select option[disabled]');
    if (secondDropdownText) {
      secondDropdownText.textContent = this.dropdownPlaceholder;
    }

    this.emitRowValues();
  }

  handleSecondDropdownChange(e: CustomEvent) {
    var target = e.target as HTMLSelectElement;
    const selectedValue = target.value;
    this.secondDropdownArr = [...this.updateSelectItemArr(this.secondDropdownArr, selectedValue)];
    this.emitRowValues();
  }

  handleInput(e) {
    const currentValue = e.target.value;
    const inputAmount = e.target.closest(".input-amount");

    if (!inputAmount) {
      return;
    }

    if (Number(currentValue) === 0) {
      inputAmount.setAttribute("error", this.errorMessageEmpty || "Cannot be empty");
      inputAmount.removeAttribute("helpertext");
      this.inputFieldValue = '';
      return;
    }

    inputAmount.removeAttribute("error");
    inputAmount.removeAttribute("helpertext");

    if (Number(currentValue) < this.minAmount) {
      inputAmount.setAttribute("error", this.errorMessageMinAmount || "Amount too low");
      this.inputFieldValue = '';
      return;
    }

    if (Number(currentValue) < this.minWarningAmount) {
      inputAmount.setAttribute("helpertext", this.warningMessageMinAmount || "Amount too low");
    }

    this.inputFieldValue = currentValue;
  }

  showDateRangeRow() {
    if (this.pnDateRangeContainer.style.display === 'none') {
      this.pnDateRangeContainer.style.display = '';
    } else {
      this.pnDateRangeContainer.style.display = 'none';
    }
  }

  handleDateSelection(e: CustomEvent) {
    const target = e.target as any;

  if (target === this.pnSingleDatePicker) {
    this.dateValue = target.value || target.start || '';
  }
  
  if (target === this.pnStartDate) {
    this.startDateValue = target.value || target.start || '';
  }
  if (target === this.pnEndDate) {
    this.endDateValue = target.value || target.start || '';
  }
}

  generateDates(e) {
    e.preventDefault();

    const allCheckboxes = this.pnDaysRow.querySelectorAll('pn-filter-checkbox');
    const selectedDays = [];
    this.generatedDatesArr = [];

    allCheckboxes.forEach(element => {
      const input = element.querySelector('input');
      if (input.value) {
        selectedDays.push(input.value);
      }
    });

    if (selectedDays.length && this.startDateValue && this.endDateValue) {
      this.daysArr.forEach(item => {
        item.selected = selectedDays.includes(item.value);
      });

      const startDate = new Date(this.startDateValue);
      const endDate = new Date(this.endDateValue);
      const disabledDateSet = new Set(this.disabledDates.split(','));

      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const year = d.getFullYear();
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;

        if (disabledDateSet.has(formattedDate)) {
          continue;
        }

        const dayOfWeek = (d.getDay() === 0) ? '7' : d.getDay().toString();
        if (selectedDays.includes(dayOfWeek)) {
          this.generatedDatesArr.push(formattedDate);
        }
      }
    }
  }


  deleteGeneratedDateRow(index) {
    this.generatedDatesArr.splice(index, 1);
    this.generatedDatesArr = [...this.generatedDatesArr];
  }

  saveGeneratedDates() {
    if (this.generatedDatesArr?.length) {
      this.dateValue = this.generatedDatesArr.join(",");
      const dateCount = this.generatedDatesArr.length;
      const buttonTextElm = this.pnShowDateRangeFiledButton.querySelector('.pn-button-content');
      buttonTextElm.innerHTML = dateCount > 0 ? `${this.dateButtonText} (${dateCount})` : this.dateButtonText;
      this.showDateRangeRow();
    }
  }

  emitRowValues() {
    this.rowValueChange.emit({
      id: this._predefinedValue.id,
      buttons: this.buttonsArray,
      firstDropdown: this.firstDropdownArr,
      secondDropdown: this.secondDropdownArr,
      input: this.inputFieldValue,
      dates: this.dateValue,
      dateRange: {
        days: this.daysArr,
        startDate: this.startDateValue,
        endDate: this.endDateValue,
      },
    });
  }

  deleteRow() {
    this.deleteRowEvent.emit(this._predefinedValue.id);
  }

  setActive(active: boolean) {
    return active ? 'connected-dropdown__button active' : 'connected-dropdown__button';
  }

  render() {
    return (
      <Host>
        <div ref={el => (this.pnConnectedDropdownSlotElement = el as HTMLElement)} class="connected-dropdown-slot-element">
          <div class="connected-dropdown-parent-element connected-dropdown">
            <div class="connected-dropdown__row__no-margin">
              <label>{this.label}</label>
            </div>

            <div class="connected-dropdown__row button-row">
              <div class="connected-dropdown__button-container option-buttons">
                {this.buttonsArray?.map(item => (
                  <button class={`${this.setActive(item.selected)}`} id={this.generateUniqueId()} onClick={this.handleOptionButtonClick.bind(this)}>
                    {item.value}
                  </button>
                ))}
              </div>

              <div class="connected-dropdown__button-container delete-button">
                {this.index > 0 ? (
                  <pn-button ref={el => (this.pnDeleteRowButton = el as HTMLElement)} small="true" icon={minus} appearance="warning" index={this.index}>
                    Delete
                  </pn-button>
                ) : null}
              </div>
            </div>

            <div class="connected-dropdown__row input-row">
              <pn-select
                ref={el => (this.pnFirstDropdown = el as HTMLElement)}
                class="pn-row-select-dropdown dropDown1-dropdown"
                label={this.firstDropdownLabel}
                language={this.languageCode}
              >
                <option value="" selected={true} disabled={true}>{this.firstDropdownPlaceholder}</option>
                {this.firstDropdownArr?.map((item) => (
                  <option value={item.value} selected={item.selected}>{item.label}</option>
                ))}
              </pn-select>
              <pn-select
                ref={el => (this.pnSecondDropdown = el as HTMLElement)}
                class="pn-row-select-dropdown dropDown2-dropdown"
                label={this.secondDropdownLabel}
                language={this.languageCode}
              >
                <option value="" selected={true} disabled={true}>{this.secondDropdownPlaceholder}</option>
                {this.secondDropdownArr?.map((item) => (
                  <option value={item.value} selected={item.selected}>{item.label}</option>
                ))}
              </pn-select>

              <pn-input
                ref={el => (this.pnInputField = el as HTMLElement)}
                class="input-amount"
                type="number"
                placeholder="0"
                label={this.inputFieldLabel}
                value={this.inputFieldValue}
                min={this.minAmount}
              ></pn-input>

              {this.allowMultipleDates ? (
                <pn-button ref={el => (this.pnShowDateRangeFiledButton = el as HTMLElement)} class="connected-dropdown__toggle-date-button" small="true" appearance="light">
                  {this.dateButtonText}
                </pn-button>
              ) : (
                <div class="connected-dropdown__date-container">
                  <pn-date-picker
                    ref={el => (this.pnSingleDatePicker = el as HTMLElement)}
                    range="false"
                    label-from={this.dateButtonText}
                    disable-weekends={true}
                    disabled-dates={this.disabledDates}
                    min-date={this.firstValidDateString}
                    language={this.languageCode}
                    placeholder={this.datePlaceholder}
                    class="connected-dropdown__date-picker"
                    start={this.dateValue?.length ? this.dateValue : null}
                  ></pn-date-picker>
                </div>
              )}
            </div>

            {this.allowMultipleDates ? (
              <div ref={el => (this.pnDateRangeContainer = el as HTMLElement)} class="connected-dropdown__date-container" style={{ display: 'none' }}>
                {this.multiDateHelperText ? (
                  <div class="connected-dropdown__row">
                    <p>{this.multiDateHelperText}</p>
                  </div>
                ) : null}

                <div class="connected-dropdown__multi-date-row">
                  <div class="connected-dropdown__date-container">
                    <pn-date-picker
                      ref={el => (this.pnStartDate = el as HTMLElement)}
                      label-from={this.startDateLabel}
                      range="false"
                      disable-weekends={true}
                      disabled-dates={this.disabledDates}
                      class="connected-dropdown__date-picker"
                      min-date={this.firstValidDateString}
                      max-date={this.lastValidDateString}
                      language={this.languageCode}
                      placeholder={this.datePlaceholder}
                    ></pn-date-picker>
                  </div>

                  <div class="connected-dropdown__date-container">
                    <pn-date-picker
                      ref={el => (this.pnEndDate = el as HTMLElement)}
                      label-from={this.endDateLabel}
                      range="false"
                      disable-weekends={true}
                      disabled-dates={this.disabledDates}
                      class="connected-dropdown__date-picker"
                      min-date={this.firstValidDateString}
                      max-date={this.lastValidDateString}
                      language={this.languageCode}
                      placeholder={this.datePlaceholder}
                    ></pn-date-picker>
                  </div>
                </div>
                <div ref={el => (this.pnDaysRow = el as HTMLElement)} class="connected-dropdown__multi-date-row">
                  {this.daysArr?.map((day: SelectItem) => (
                    <div class="connected-dropdown__checkbox">
                      <pn-filter-checkbox checkboxid={'day-' + day.value} name={'__' + day.label} value={day.value} checked={day.selected}></pn-filter-checkbox>
                      <label htmlFor={'day-' + day.value}>{day.label}</label>
                    </div>
                  ))}
                </div>

                <div class="connected-dropdown__row">
                  <pn-button ref={el => (this.pnGenerateDatesButton = el as HTMLElement)} small="true">
                    {this.generateDatesButtonText}
                  </pn-button>
                </div>

                <div ref={el => (this.pnGeneratedDatesSlot = el as HTMLElement)} class="connected-dropdown__date-slot">
                  {this.generatedDatesArr?.map((date: string, i) => (
                    <div class="connected-dropdown__row generated-date-row">
                      <p>{date}</p>
                      <pn-button index={i} small="true" icon={minus} appearance="warning" id={'delete-date-' + i} onClick={this.deleteGeneratedDateRow.bind(this, i)}>
                        {date}
                      </pn-button>
                    </div>
                  ))}

                  {!this.generatedDatesArr?.length ? <p>{this.noDatesText}</p> : null}
                </div>

                <div class="connected-dropdown__row">
                  <pn-button ref={el => (this.pnSaveGeneratedDatesButton = el as HTMLElement)} class="connected-dropdown__button-right" small="true">
                    {this.saveDateButtonText}
                  </pn-button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </Host>
    );
  }
}
